import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  DateFormatter,
  CELL_RENDERER,
  SFDatagrid,
  SFPageContainer,
  SFActionBar,
  ACTION_BAR_ACTION_NAMES,
  ACTION_BAR_ACTION_TYPES,
  SFAddIcon,
  getStyled,
  sfDatagridHelpers,
} from "@sf-core";
import { Logger } from "@base-provider";
import { useSelector } from "react-redux";
import { GridNames } from "../../constants/gridConfig";
import { ExportFileNames } from "../../constants/exportFileNames";
import { Claims } from "../../constants/claims";
import { API_PARAM } from "../../constants/apiParams";
import { exportTenantsGrid } from "../../services/tenantUserService";
import { PATHS } from "../../constants/paths";

const { styled } = getStyled();

const SFStyledDataGrid = styled(SFDatagrid)(() => ({
  // Even with fitScreen prop, the below class of grid is not taking the full width of the container.
  // Added the below style to fix the issue.
  "& .ag-center-cols-container": {
    width: "100% !important",
  },
}));

const Tenants = () => {
  const gridRef = useRef(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tenantsGridLayout = useSelector((state) =>
    state.gridLayout.find((grid) => grid.gridName === GridNames.TENANTS_GRID)
  );

  const rowHandler = (rowData = null) => {
    Logger.of("App.tenantsGrid.rowHandler").info({ rowData });
    navigate(PATHS.ADMIN_TENANTS_TENANT_DETAILS.replace(/:id/, rowData.id));
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: t("tenantsScreen.tenantsGrid.idColumn"),
        field: "id",
        hide: true,
        filter: false,
      },
      {
        headerName: t("tenantsScreen.tenantsGrid.nameColumn"),
        field: "name",
        maxWidth: 180,
        filter: true,
        cellRenderer: CELL_RENDERER.EDIT_CLICKABLE,
        cellRendererParams: {
          onClick: rowHandler,
        },
        filterParams: {
          order: 1,
          advancedQuery: true,
        },
      },
      {
        headerName: t("tenantsScreen.tenantsGrid.createdAtColumn"),
        field: "createdAt",
        filter: false,
        valueFormatter: DateFormatter,
        maxWidth: 180,
        filterParams: {
          advancedQuery: true,
        },
      },
      {
        headerName: t("tenantsScreen.tenantsGrid.createdByColumn"),
        field: "createdBy",
        maxWidth: 180,
        filterParams: {
          advancedQuery: true,
        },
      },
      {
        headerName: t("tenantsScreen.tenantsGrid.updatedAtColumn"),
        field: "updatedAt",
        filter: false,
        valueFormatter: DateFormatter,
        maxWidth: 180,
        filterParams: {
          advancedQuery: true,
        },
      },
      {
        headerName: t("tenantsScreen.tenantsGrid.updatedByColumn"),
        field: "updatedBy",
        maxWidth: 180,
        filterParams: {
          advancedQuery: true,
        },
      },
      {
        headerName: t("tenantsScreen.tenantsGrid.activeColumn"),
        field: "isActive",
        cellRenderer: CELL_RENDERER.BOOLEAN,
        headerClass: "header-text-center",
        maxWidth: 180,
        filter: "boolean",
        filterParams: {
          order: 2,
        },
      },
      {
        headerName: t("tenantsScreen.tenantsGrid.deletedAtColumn"),
        field: "deletedAt",
        filter: false,
        maxWidth: 180,
        valueFormatter: DateFormatter,
      },
      {
        headerName: t("tenantsScreen.tenantsGrid.deletedByColumn"),
        field: "deletedBy",
        maxWidth: 180,
      },
    ],
    []
  );

  const messages = {
    addSuccess: t("tenantsScreen.messages.addSuccess"),
    editSuccess: t("tenantsScreen.messages.editSuccess"),
    deleteSuccess: t("tenantsScreen.messages.deleteSuccess"),
    restoreSuccess: t("tenantsScreen.messages.restoreSuccess"),
  };

  const handleTenantsDownload = () => {
    exportTenantsGrid(tenantsGridLayout?.filterModel ?? {});
  };

  const handlerNewTenantsFramework = () => {
    navigate(PATHS.ADMIN_TENANTS_ADD_TENANT);
  };

  const actionDefs = [
    {
      name: ACTION_BAR_ACTION_NAMES.DOWNLOAD,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      children: [
        {
          name: t("tenantUsersScreen.actionBar.tenantUsersScreen"),
          label: t("tenantsScreen.tenantsGridTitle"),
          onClick: () => {
            handleTenantsDownload();
          },
        },
      ],
    },
    {
      name: ACTION_BAR_ACTION_NAMES.FILTER,
      type: ACTION_BAR_ACTION_TYPES.ICON,
      filterParams: { gridName: GridNames.TENANTS_GRID, advancedQuery: true },
    },
    {
      name: ACTION_BAR_ACTION_NAMES.ADD,
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      onClick: handlerNewTenantsFramework,
      permissions: [Claims.AdminTenantsAdd],
      buttonProps: {
        label: t("tenantsScreen.addButton"),
        variant: "primary",
        icon: <SFAddIcon />,
        iconPlacement: "start",
      },
    },
  ];

  return (
    <SFPageContainer disableGutters>
      <SFActionBar
        actionDefs={actionDefs}
        titleProps={{
          title: t("tenantsScreen.tenantsGridTitle"),
        }}
        search
        searchProps={{
          onInputChange: (value) => {
            sfDatagridHelpers.updateSearchTextFilter(gridRef, value);
          },
          gridName: GridNames.TENANTS_GRID,
        }}
      />
      <SFStyledDataGrid
        ref={gridRef}
        basicFiltering
        persistState
        fitScreen
        messages={messages}
        canDownload={false}
        exportFileName={ExportFileNames.TENANTS}
        columnDefs={columnDefs}
        gridName={GridNames.TENANTS_GRID}
        datasourceUrl={API_PARAM.TENANTS_GRID}
        filterValuesUrl={API_PARAM.TENANTS_COL_FILTER}
        permissions={{
          export: Claims.AdminTenantsExport,
          add: Claims.AdminTenantsAdd,
          delete: Claims.AdminTenantsDelete,
          edit: Claims.AdminTenantsEdit,
          view: Claims.AdminTenantsView,
        }}
      />
    </SFPageContainer>
  );
};

export default Tenants;
